import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { 
  TrendingUp,
  TrendingDown,
  Package,
  ShoppingCart,
  DollarSign,
  BarChart3,
  Calendar,
  AlertTriangle,
  RefreshCw,
  Download,
  PieChart
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  PieChart as RechartsPieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  Area,
  AreaChart
} from 'recharts';

const API = process.env.REACT_APP_BACKEND_URL;

const NASReports = () => {
  const [activeTab, setActiveTab] = useState('sales');
  const [period, setPeriod] = useState('month');
  const [customStart, setCustomStart] = useState('');
  const [customEnd, setCustomEnd] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  
  const [salesReport, setSalesReport] = useState(null);
  const [inventoryReport, setInventoryReport] = useState(null);
  const [ordersReport, setOrdersReport] = useState(null);
  const [profitReport, setProfitReport] = useState(null);
  const [forecastData, setForecastData] = useState(null);
  const [salesTrends, setSalesTrends] = useState(null);
  const [inventoryHealth, setInventoryHealth] = useState(null);
  
  // Chart data states
  const [salesTrendData, setSalesTrendData] = useState([]);
  const [categoryData, setCategoryData] = useState([]);
  const [inventoryStatusData, setInventoryStatusData] = useState([]);
  const [topProductsData, setTopProductsData] = useState([]);
  
  // Chart colors
  const CHART_COLORS = ['#22c55e', '#3b82f6', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899', '#06b6d4'];
  
  useEffect(() => {
    loadReport(activeTab);
    loadChartData();
  }, [activeTab, period]);
  
  const loadChartData = async () => {
    try {
      const [trendRes, categoryRes, statusRes, topRes] = await Promise.all([
        axios.get(`${API}/api/nas/reports/charts/sales-trend?days=30`),
        axios.get(`${API}/api/nas/reports/charts/category-distribution`),
        axios.get(`${API}/api/nas/reports/charts/inventory-status`),
        axios.get(`${API}/api/nas/reports/charts/top-products?period=${period}`)
      ]);
      
      setSalesTrendData(trendRes.data);
      setCategoryData(categoryRes.data);
      setInventoryStatusData(statusRes.data);
      setTopProductsData(topRes.data);
    } catch (error) {
      console.error('Error loading chart data:', error);
    }
  };
  
  const loadReport = async (reportType) => {
    setIsLoading(true);
    try {
      let url = `${API}/api/nas/reports/${reportType}`;
      
      // Handle forecast tab separately - uses analytics endpoints
      if (reportType === 'forecast') {
        const [forecastRes, trendsRes, healthRes] = await Promise.all([
          axios.get(`${API}/api/nas/analytics/forecast?days=30`),
          axios.get(`${API}/api/nas/analytics/sales-trends?days=30`),
          axios.get(`${API}/api/nas/analytics/inventory-health`)
        ]);
        setForecastData(forecastRes.data.forecasts || []);
        setSalesTrends(trendsRes.data);
        setInventoryHealth(healthRes.data);
        setIsLoading(false);
        return;
      }
      
      if (reportType !== 'inventory') {
        url += `?period=${period}`;
        if (period === 'custom' && customStart && customEnd) {
          url += `&start_date=${customStart}&end_date=${customEnd}`;
        }
      }
      
      const response = await axios.get(url);
      
      switch (reportType) {
        case 'sales':
          setSalesReport(response.data);
          break;
        case 'inventory':
          setInventoryReport(response.data);
          break;
        case 'orders':
          setOrdersReport(response.data);
          break;
        case 'profit':
          setProfitReport(response.data);
          break;
      }
    } catch (error) {
      console.error(`Error loading ${reportType} report:`, error);
      toast.error(`Failed to load ${reportType} report`);
    } finally {
      setIsLoading(false);
    }
  };
  
  const formatCurrency = (amount) => {
    return `₱${(amount || 0).toLocaleString()}`;
  };
  
  const handleCustomDateSearch = () => {
    if (customStart && customEnd) {
      setPeriod('custom');
      loadReport(activeTab);
    } else {
      toast.error('Please select both start and end dates');
    }
  };
  
  const handleExport = async (reportType) => {
    try {
      let url = `${API}/api/nas/reports/export/${reportType}?period=${period}`;
      if (period === 'custom' && customStart && customEnd) {
        url += `&start_date=${customStart}&end_date=${customEnd}`;
      }
      
      // Open download link
      window.open(url, '_blank');
      toast.success(`Exporting ${reportType} report...`);
    } catch (error) {
      toast.error(`Failed to export ${reportType} report`);
    }
  };
  
  return (
    <div className="p-4 lg:p-6 space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-800">Reports & Analytics</h1>
          <p className="text-slate-500">Business insights for NAS</p>
        </div>
        <div className="flex gap-2">
          <Button 
            variant="outline" 
            onClick={() => handleExport(activeTab)}
            className="bg-green-50 border-green-300 text-green-700 hover:bg-green-100"
          >
            <Download size={18} className="mr-2" />
            Export Excel
          </Button>
          <Button variant="outline" onClick={() => loadReport(activeTab)} disabled={isLoading}>
            <RefreshCw size={18} className={isLoading ? 'animate-spin mr-2' : 'mr-2'} />
            Refresh
          </Button>
        </div>
      </div>
      
      {/* Period Selector */}
      <Card className="bg-white">
        <CardContent className="p-4">
          <div className="flex flex-wrap items-end gap-4">
            <div>
              <Label className="text-sm">Period</Label>
              <Select value={period} onValueChange={setPeriod}>
                <SelectTrigger className="w-40">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="today">Today</SelectItem>
                  <SelectItem value="week">Last 7 Days</SelectItem>
                  <SelectItem value="month">This Month</SelectItem>
                  <SelectItem value="custom">Custom Range</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            {period === 'custom' && (
              <>
                <div>
                  <Label className="text-sm">Start Date</Label>
                  <Input
                    type="date"
                    value={customStart}
                    onChange={(e) => setCustomStart(e.target.value)}
                    className="w-40"
                  />
                </div>
                <div>
                  <Label className="text-sm">End Date</Label>
                  <Input
                    type="date"
                    value={customEnd}
                    onChange={(e) => setCustomEnd(e.target.value)}
                    className="w-40"
                  />
                </div>
                <Button onClick={handleCustomDateSearch}>
                  <Calendar size={16} className="mr-2" />
                  Apply
                </Button>
              </>
            )}
          </div>
        </CardContent>
      </Card>
      
      {/* Report Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid grid-cols-5 w-full max-w-2xl">
          <TabsTrigger value="sales">Sales</TabsTrigger>
          <TabsTrigger value="inventory">Inventory</TabsTrigger>
          <TabsTrigger value="orders">Orders</TabsTrigger>
          <TabsTrigger value="profit">Profit</TabsTrigger>
          <TabsTrigger value="forecast">Forecasting</TabsTrigger>
        </TabsList>
        
        {/* Sales Report */}
        <TabsContent value="sales" className="space-y-4 mt-4">
          {isLoading ? (
            <div className="text-center py-10">
              <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-green-600 mx-auto"></div>
            </div>
          ) : salesReport ? (
            <>
              {/* Summary Cards */}
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                <Card className="bg-white">
                  <CardContent className="p-4">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-lg bg-green-100 flex items-center justify-center">
                        <DollarSign className="text-green-600" size={20} />
                      </div>
                      <div>
                        <p className="text-sm text-slate-500">Total Revenue</p>
                        <p className="text-xl font-bold text-green-600">{formatCurrency(salesReport.summary.totalRevenue)}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
                <Card className="bg-white">
                  <CardContent className="p-4">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
                        <ShoppingCart className="text-blue-600" size={20} />
                      </div>
                      <div>
                        <p className="text-sm text-slate-500">Transactions</p>
                        <p className="text-xl font-bold">{salesReport.summary.totalTransactions}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
                <Card className="bg-white">
                  <CardContent className="p-4">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-lg bg-purple-100 flex items-center justify-center">
                        <Package className="text-purple-600" size={20} />
                      </div>
                      <div>
                        <p className="text-sm text-slate-500">Wholesale</p>
                        <p className="text-xl font-bold">{formatCurrency(salesReport.summary.wholesaleRevenue)}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
                <Card className="bg-white">
                  <CardContent className="p-4">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-lg bg-amber-100 flex items-center justify-center">
                        <BarChart3 className="text-amber-600" size={20} />
                      </div>
                      <div>
                        <p className="text-sm text-slate-500">Retail</p>
                        <p className="text-xl font-bold">{formatCurrency(salesReport.summary.retailRevenue)}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>
              
              {/* Sales Trend Chart */}
              <Card className="bg-white">
                <CardHeader>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <TrendingUp size={20} className="text-green-600" />
                    Sales Trend (Last 30 Days)
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {salesTrendData.length > 0 ? (
                    <ResponsiveContainer width="100%" height={300}>
                      <AreaChart data={salesTrendData}>
                        <defs>
                          <linearGradient id="colorTotal" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="5%" stopColor="#22c55e" stopOpacity={0.3}/>
                            <stop offset="95%" stopColor="#22c55e" stopOpacity={0}/>
                          </linearGradient>
                        </defs>
                        <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                        <XAxis dataKey="day" tick={{fontSize: 12}} stroke="#94a3b8" />
                        <YAxis tick={{fontSize: 12}} stroke="#94a3b8" tickFormatter={(v) => `₱${(v/1000).toFixed(0)}k`} />
                        <Tooltip 
                          formatter={(value) => [`₱${value.toLocaleString()}`, '']}
                          contentStyle={{borderRadius: '8px', border: '1px solid #e2e8f0'}}
                        />
                        <Legend />
                        <Area type="monotone" dataKey="total" name="Total Sales" stroke="#22c55e" fill="url(#colorTotal)" strokeWidth={2} />
                        <Line type="monotone" dataKey="wholesale" name="Wholesale" stroke="#3b82f6" strokeWidth={2} dot={false} />
                        <Line type="monotone" dataKey="retail" name="Retail" stroke="#f59e0b" strokeWidth={2} dot={false} />
                      </AreaChart>
                    </ResponsiveContainer>
                  ) : (
                    <div className="h-[300px] flex items-center justify-center text-slate-400">
                      No sales data available
                    </div>
                  )}
                </CardContent>
              </Card>
              
              {/* Top Products Chart */}
              <Card className="bg-white">
                <CardHeader>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <BarChart3 size={20} className="text-blue-600" />
                    Top Selling Products
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {topProductsData.length > 0 ? (
                    <ResponsiveContainer width="100%" height={300}>
                      <BarChart data={topProductsData} layout="vertical">
                        <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                        <XAxis type="number" tick={{fontSize: 12}} stroke="#94a3b8" tickFormatter={(v) => `₱${(v/1000).toFixed(0)}k`} />
                        <YAxis dataKey="name" type="category" width={120} tick={{fontSize: 11}} stroke="#94a3b8" />
                        <Tooltip 
                          formatter={(value) => [`₱${value.toLocaleString()}`, 'Revenue']}
                          contentStyle={{borderRadius: '8px', border: '1px solid #e2e8f0'}}
                        />
                        <Bar dataKey="revenue" fill="#3b82f6" radius={[0, 4, 4, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  ) : (
                    <div className="h-[300px] flex items-center justify-center text-slate-400">
                      No product data available
                    </div>
                  )}
                </CardContent>
              </Card>
              
              {/* Sales by Product */}
              <Card className="bg-white">
                <CardHeader>
                  <CardTitle className="text-lg">Sales by Product</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead className="bg-slate-50">
                        <tr>
                          <th className="text-left p-3">Product</th>
                          <th className="text-right p-3">Wholesale Qty</th>
                          <th className="text-right p-3">Retail Qty</th>
                          <th className="text-right p-3">Revenue</th>
                          <th className="text-right p-3">Transactions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {salesReport.byProduct.length === 0 ? (
                          <tr>
                            <td colSpan={5} className="text-center py-8 text-slate-500">No sales data for this period</td>
                          </tr>
                        ) : (
                          salesReport.byProduct.map((item, idx) => (
                            <tr key={idx} className="border-b">
                              <td className="p-3 font-medium">{item.productName}</td>
                              <td className="p-3 text-right">{item.wholesaleQuantity}</td>
                              <td className="p-3 text-right">{item.retailQuantity}</td>
                              <td className="p-3 text-right text-green-600 font-medium">{formatCurrency(item.totalRevenue)}</td>
                              <td className="p-3 text-right">{item.transactionCount}</td>
                            </tr>
                          ))
                        )}
                      </tbody>
                    </table>
                  </div>
                </CardContent>
              </Card>
            </>
          ) : null}
        </TabsContent>
        
        {/* Inventory Report */}
        <TabsContent value="inventory" className="space-y-4 mt-4">
          {isLoading ? (
            <div className="text-center py-10">
              <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-green-600 mx-auto"></div>
            </div>
          ) : inventoryReport ? (
            <>
              {/* Summary Cards */}
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                <Card className="bg-white">
                  <CardContent className="p-4">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
                        <Package className="text-blue-600" size={20} />
                      </div>
                      <div>
                        <p className="text-sm text-slate-500">Total Products</p>
                        <p className="text-xl font-bold">{inventoryReport.summary.totalProducts}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
                <Card className="bg-white">
                  <CardContent className="p-4">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-lg bg-green-100 flex items-center justify-center">
                        <DollarSign className="text-green-600" size={20} />
                      </div>
                      <div>
                        <p className="text-sm text-slate-500">Inventory Value</p>
                        <p className="text-xl font-bold text-green-600">{formatCurrency(inventoryReport.summary.totalInventoryValue)}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
                <Card className="bg-white">
                  <CardContent className="p-4">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-lg bg-amber-100 flex items-center justify-center">
                        <AlertTriangle className="text-amber-600" size={20} />
                      </div>
                      <div>
                        <p className="text-sm text-slate-500">Low Stock</p>
                        <p className="text-xl font-bold text-amber-600">{inventoryReport.summary.lowStockCount}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
                <Card className="bg-white">
                  <CardContent className="p-4">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-lg bg-red-100 flex items-center justify-center">
                        <AlertTriangle className="text-red-600" size={20} />
                      </div>
                      <div>
                        <p className="text-sm text-slate-500">Out of Stock</p>
                        <p className="text-xl font-bold text-red-600">{inventoryReport.summary.outOfStockCount}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>
              
              {/* Inventory Charts Row */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                {/* Inventory Status Pie Chart */}
                <Card className="bg-white">
                  <CardHeader>
                    <CardTitle className="text-lg flex items-center gap-2">
                      <PieChart size={20} className="text-purple-600" />
                      Stock Status Distribution
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    {inventoryStatusData.length > 0 ? (
                      <ResponsiveContainer width="100%" height={250}>
                        <RechartsPieChart>
                          <Pie
                            data={inventoryStatusData}
                            cx="50%"
                            cy="50%"
                            innerRadius={60}
                            outerRadius={90}
                            paddingAngle={3}
                            dataKey="value"
                            label={({name, value}) => `${name}: ${value}`}
                          >
                            {inventoryStatusData.map((entry, index) => (
                              <Cell key={`cell-${index}`} fill={entry.color} />
                            ))}
                          </Pie>
                          <Tooltip />
                        </RechartsPieChart>
                      </ResponsiveContainer>
                    ) : (
                      <div className="h-[250px] flex items-center justify-center text-slate-400">
                        No inventory data
                      </div>
                    )}
                  </CardContent>
                </Card>
                
                {/* Category Distribution Chart */}
                <Card className="bg-white">
                  <CardHeader>
                    <CardTitle className="text-lg flex items-center gap-2">
                      <BarChart3 size={20} className="text-blue-600" />
                      Products by Category
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    {categoryData.length > 0 ? (
                      <ResponsiveContainer width="100%" height={250}>
                        <BarChart data={categoryData}>
                          <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                          <XAxis dataKey="name" tick={{fontSize: 11}} stroke="#94a3b8" angle={-20} textAnchor="end" height={60} />
                          <YAxis tick={{fontSize: 12}} stroke="#94a3b8" />
                          <Tooltip 
                            formatter={(value, name) => [name === 'count' ? value : `₱${value.toLocaleString()}`, name === 'count' ? 'Products' : 'Value']}
                            contentStyle={{borderRadius: '8px', border: '1px solid #e2e8f0'}}
                          />
                          <Bar dataKey="count" name="Products" fill="#3b82f6" radius={[4, 4, 0, 0]} />
                        </BarChart>
                      </ResponsiveContainer>
                    ) : (
                      <div className="h-[250px] flex items-center justify-center text-slate-400">
                        No category data
                      </div>
                    )}
                  </CardContent>
                </Card>
              </div>
              
              {/* Alerts */}
              {(inventoryReport.alerts.lowStock.length > 0 || inventoryReport.alerts.outOfStock.length > 0) && (
                <Card className="bg-amber-50 border-amber-200">
                  <CardHeader>
                    <CardTitle className="text-lg text-amber-800 flex items-center gap-2">
                      <AlertTriangle size={20} />
                      Stock Alerts
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-2">
                      {inventoryReport.alerts.outOfStock.length > 0 && (
                        <div className="p-3 bg-red-100 rounded-lg">
                          <p className="font-medium text-red-800">Out of Stock ({inventoryReport.alerts.outOfStock.length})</p>
                          <p className="text-sm text-red-700">{inventoryReport.alerts.outOfStock.join(', ')}</p>
                        </div>
                      )}
                      {inventoryReport.alerts.lowStock.length > 0 && (
                        <div className="p-3 bg-amber-100 rounded-lg">
                          <p className="font-medium text-amber-800">Low Stock ({inventoryReport.alerts.lowStock.length})</p>
                          <p className="text-sm text-amber-700">{inventoryReport.alerts.lowStock.join(', ')}</p>
                        </div>
                      )}
                    </div>
                  </CardContent>
                </Card>
              )}
              
              {/* Inventory Table */}
              <Card className="bg-white">
                <CardHeader>
                  <CardTitle className="text-lg">Inventory Valuation</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead className="bg-slate-50">
                        <tr>
                          <th className="text-left p-3">Product</th>
                          <th className="text-right p-3">Bodega</th>
                          <th className="text-right p-3">Shop (W)</th>
                          <th className="text-right p-3">Shop (R)</th>
                          <th className="text-right p-3">Value</th>
                          <th className="text-center p-3">Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        {inventoryReport.items.map((item, idx) => (
                          <tr key={idx} className="border-b">
                            <td className="p-3">
                              <div className="font-medium">{item.name}</div>
                              <div className="text-xs text-slate-500">{item.category}</div>
                            </td>
                            <td className="p-3 text-right">{item.bodegaStock}</td>
                            <td className="p-3 text-right">{item.shopWholesale}</td>
                            <td className="p-3 text-right">{item.shopRetail}</td>
                            <td className="p-3 text-right font-medium">{formatCurrency(item.totalValue)}</td>
                            <td className="p-3 text-center">
                              <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                                item.status === 'ok' ? 'bg-green-100 text-green-700' :
                                item.status === 'low' ? 'bg-amber-100 text-amber-700' :
                                'bg-red-100 text-red-700'
                              }`}>
                                {item.status === 'ok' ? 'OK' : item.status === 'low' ? 'Low' : 'Out'}
                              </span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </CardContent>
              </Card>
            </>
          ) : null}
        </TabsContent>
        
        {/* Orders Report */}
        <TabsContent value="orders" className="space-y-4 mt-4">
          {isLoading ? (
            <div className="text-center py-10">
              <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-green-600 mx-auto"></div>
            </div>
          ) : ordersReport ? (
            <>
              {/* Summary Cards */}
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                <Card className="bg-white">
                  <CardContent className="p-4">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
                        <ShoppingCart className="text-blue-600" size={20} />
                      </div>
                      <div>
                        <p className="text-sm text-slate-500">Total Orders</p>
                        <p className="text-xl font-bold">{ordersReport.summary.totalOrders}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
                <Card className="bg-white">
                  <CardContent className="p-4">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-lg bg-green-100 flex items-center justify-center">
                        <DollarSign className="text-green-600" size={20} />
                      </div>
                      <div>
                        <p className="text-sm text-slate-500">Total Amount</p>
                        <p className="text-xl font-bold">{formatCurrency(ordersReport.summary.totalAmount)}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
                <Card className="bg-white">
                  <CardContent className="p-4">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-lg bg-amber-100 flex items-center justify-center">
                        <TrendingUp className="text-amber-600" size={20} />
                      </div>
                      <div>
                        <p className="text-sm text-slate-500">Avg Order Value</p>
                        <p className="text-xl font-bold">{formatCurrency(ordersReport.summary.averageOrderValue)}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
                <Card className="bg-white">
                  <CardContent className="p-4">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-lg bg-purple-100 flex items-center justify-center">
                        <Package className="text-purple-600" size={20} />
                      </div>
                      <div>
                        <p className="text-sm text-slate-500">Delivered</p>
                        <p className="text-xl font-bold">{ordersReport.summary.deliveredOrders}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>
              
              {/* By Supplier */}
              <Card className="bg-white">
                <CardHeader>
                  <CardTitle className="text-lg">Orders by Supplier</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {ordersReport.bySupplier.length === 0 ? (
                      <p className="text-center py-8 text-slate-500">No orders data for this period</p>
                    ) : (
                      ordersReport.bySupplier.map((supplier, idx) => (
                        <div key={idx} className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
                          <div>
                            <p className="font-medium">{supplier.name}</p>
                            <p className="text-sm text-slate-500">{supplier.orderCount} orders</p>
                          </div>
                          <div className="text-right">
                            <p className="text-lg font-bold text-green-600">{formatCurrency(supplier.totalAmount)}</p>
                            <p className="text-xs text-slate-500">+ {formatCurrency(supplier.totalExpenses)} expenses</p>
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                </CardContent>
              </Card>
            </>
          ) : null}
        </TabsContent>
        
        {/* Profit Report */}
        <TabsContent value="profit" className="space-y-4 mt-4">
          {isLoading ? (
            <div className="text-center py-10">
              <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-green-600 mx-auto"></div>
            </div>
          ) : profitReport ? (
            <>
              {/* Summary Cards */}
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                <Card className="bg-white">
                  <CardContent className="p-4">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-lg bg-green-100 flex items-center justify-center">
                        <TrendingUp className="text-green-600" size={20} />
                      </div>
                      <div>
                        <p className="text-sm text-slate-500">Revenue</p>
                        <p className="text-xl font-bold text-green-600">{formatCurrency(profitReport.revenue.total)}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
                <Card className="bg-white">
                  <CardContent className="p-4">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-lg bg-red-100 flex items-center justify-center">
                        <TrendingDown className="text-red-600" size={20} />
                      </div>
                      <div>
                        <p className="text-sm text-slate-500">Total Costs</p>
                        <p className="text-xl font-bold text-red-600">{formatCurrency(profitReport.costs.totalCost)}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
                <Card className="bg-white">
                  <CardContent className="p-4">
                    <div className="flex items-center gap-3">
                      <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                        profitReport.profit.netProfit >= 0 ? 'bg-green-100' : 'bg-red-100'
                      }`}>
                        <DollarSign className={profitReport.profit.netProfit >= 0 ? 'text-green-600' : 'text-red-600'} size={20} />
                      </div>
                      <div>
                        <p className="text-sm text-slate-500">Net Profit</p>
                        <p className={`text-xl font-bold ${profitReport.profit.netProfit >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                          {formatCurrency(profitReport.profit.netProfit)}
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
                <Card className="bg-white">
                  <CardContent className="p-4">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
                        <BarChart3 className="text-blue-600" size={20} />
                      </div>
                      <div>
                        <p className="text-sm text-slate-500">Profit Margin</p>
                        <p className="text-xl font-bold">{profitReport.profit.profitMargin}%</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>
              
              {/* Breakdown */}
              <div className="grid md:grid-cols-2 gap-4">
                <Card className="bg-white">
                  <CardHeader>
                    <CardTitle className="text-lg text-green-700">Revenue Breakdown</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    <div className="flex justify-between p-3 bg-green-50 rounded-lg">
                      <span>Wholesale Revenue</span>
                      <span className="font-medium">{formatCurrency(profitReport.revenue.wholesale)}</span>
                    </div>
                    <div className="flex justify-between p-3 bg-green-50 rounded-lg">
                      <span>Retail Revenue</span>
                      <span className="font-medium">{formatCurrency(profitReport.revenue.retail)}</span>
                    </div>
                    <div className="flex justify-between p-3 bg-green-100 rounded-lg font-bold">
                      <span>Total Revenue</span>
                      <span className="text-green-700">{formatCurrency(profitReport.revenue.total)}</span>
                    </div>
                  </CardContent>
                </Card>
                
                <Card className="bg-white">
                  <CardHeader>
                    <CardTitle className="text-lg text-red-700">Cost Breakdown</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    <div className="flex justify-between p-3 bg-red-50 rounded-lg">
                      <span>Product Cost</span>
                      <span className="font-medium">{formatCurrency(profitReport.costs.productCost)}</span>
                    </div>
                    <div className="flex justify-between p-3 bg-red-50 rounded-lg">
                      <span>Expenses</span>
                      <span className="font-medium">{formatCurrency(profitReport.costs.expenses)}</span>
                    </div>
                    <div className="flex justify-between p-3 bg-red-100 rounded-lg font-bold">
                      <span>Total Cost</span>
                      <span className="text-red-700">{formatCurrency(profitReport.costs.totalCost)}</span>
                    </div>
                  </CardContent>
                </Card>
              </div>
              
              {/* Daily Breakdown */}
              {profitReport.dailyBreakdown.length > 0 && (
                <Card className="bg-white">
                  <CardHeader>
                    <CardTitle className="text-lg">Daily Performance</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead className="bg-slate-50">
                          <tr>
                            <th className="text-left p-3">Date</th>
                            <th className="text-right p-3">Revenue</th>
                            <th className="text-right p-3">Cost</th>
                            <th className="text-right p-3">Profit</th>
                          </tr>
                        </thead>
                        <tbody>
                          {profitReport.dailyBreakdown.map((day, idx) => (
                            <tr key={idx} className="border-b">
                              <td className="p-3">{day.date}</td>
                              <td className="p-3 text-right text-green-600">{formatCurrency(day.revenue)}</td>
                              <td className="p-3 text-right text-red-600">{formatCurrency(day.cost)}</td>
                              <td className={`p-3 text-right font-medium ${day.profit >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                                {formatCurrency(day.profit)}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </CardContent>
                </Card>
              )}
            </>
          ) : null}
        </TabsContent>
        
        {/* Forecasting Tab */}
        <TabsContent value="forecast" className="space-y-4 mt-4">
          {isLoading ? (
            <div className="text-center py-10">
              <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-indigo-600 mx-auto"></div>
            </div>
          ) : (
            <>
              {/* Inventory Health Summary */}
              {inventoryHealth && (
                <Card className="bg-white">
                  <CardHeader>
                    <CardTitle className="text-lg flex items-center gap-2">
                      <BarChart3 className="text-indigo-600" size={20} />
                      Inventory Health Score
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      <div className="text-center p-4 bg-gradient-to-br from-indigo-50 to-indigo-100 rounded-lg">
                        <div className="text-4xl font-bold text-indigo-600">{inventoryHealth.healthScore || 0}%</div>
                        <p className="text-sm text-indigo-700">Overall Health</p>
                      </div>
                      <div className="text-center p-4 bg-green-50 rounded-lg">
                        <div className="text-2xl font-bold text-green-600">{inventoryHealth.metrics?.totalProducts || 0}</div>
                        <p className="text-sm text-slate-600">Total Products</p>
                      </div>
                      <div className="text-center p-4 bg-amber-50 rounded-lg">
                        <div className="text-2xl font-bold text-amber-600">{inventoryHealth.metrics?.lowStockProducts || 0}</div>
                        <p className="text-sm text-slate-600">Low Stock</p>
                      </div>
                      <div className="text-center p-4 bg-red-50 rounded-lg">
                        <div className="text-2xl font-bold text-red-600">{inventoryHealth.metrics?.outOfStockProducts || 0}</div>
                        <p className="text-sm text-slate-600">Out of Stock</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              )}
              
              {/* Sales Trends Summary */}
              {salesTrends && (
                <Card className="bg-white">
                  <CardHeader>
                    <CardTitle className="text-lg flex items-center gap-2">
                      <TrendingUp className="text-green-600" size={20} />
                      Sales Trends (Last 30 Days)
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                      <div className="p-4 bg-green-50 rounded-lg">
                        <p className="text-sm text-slate-600">Total Revenue</p>
                        <p className="text-xl font-bold text-green-600">{formatCurrency(salesTrends.summary?.totalRevenue)}</p>
                      </div>
                      <div className="p-4 bg-blue-50 rounded-lg">
                        <p className="text-sm text-slate-600">Transactions</p>
                        <p className="text-xl font-bold text-blue-600">{salesTrends.summary?.totalTransactions || 0}</p>
                      </div>
                      <div className="p-4 bg-purple-50 rounded-lg">
                        <p className="text-sm text-slate-600">Avg Transaction</p>
                        <p className="text-xl font-bold text-purple-600">{formatCurrency(salesTrends.summary?.averageTransactionValue)}</p>
                      </div>
                      <div className="p-4 bg-amber-50 rounded-lg">
                        <p className="text-sm text-slate-600">Growth Rate</p>
                        <p className={`text-xl font-bold ${(salesTrends.summary?.growthRate || 0) >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                          {salesTrends.summary?.growthRate >= 0 ? '+' : ''}{salesTrends.summary?.growthRate || 0}%
                        </p>
                      </div>
                    </div>
                    
                    {/* Daily Sales Chart */}
                    {salesTrends.dailyTrend && salesTrends.dailyTrend.length > 0 && (
                      <div className="h-64">
                        <ResponsiveContainer width="100%" height="100%">
                          <AreaChart data={salesTrends.dailyTrend}>
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis 
                              dataKey="date" 
                              tickFormatter={(value) => value.slice(5)}
                              fontSize={12}
                            />
                            <YAxis 
                              tickFormatter={(value) => `₱${(value/1000).toFixed(0)}k`}
                              fontSize={12}
                            />
                            <Tooltip 
                              formatter={(value) => [`₱${value.toLocaleString()}`, 'Revenue']}
                              labelFormatter={(label) => `Date: ${label}`}
                            />
                            <Area 
                              type="monotone" 
                              dataKey="totalAmount" 
                              stroke="#22c55e" 
                              fill="#bbf7d0" 
                              name="Revenue"
                            />
                          </AreaChart>
                        </ResponsiveContainer>
                      </div>
                    )}
                  </CardContent>
                </Card>
              )}
              
              {/* Stock Forecast Table */}
              {forecastData && forecastData.length > 0 && (
                <Card className="bg-white">
                  <CardHeader>
                    <CardTitle className="text-lg flex items-center gap-2">
                      <AlertTriangle className="text-amber-600" size={20} />
                      Stock Forecasting & Reorder Recommendations
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="border-b bg-slate-50">
                            <th className="text-left p-3">Product</th>
                            <th className="text-right p-3">Current Stock</th>
                            <th className="text-right p-3">Avg Daily Sales</th>
                            <th className="text-right p-3">Days Until Stockout</th>
                            <th className="text-center p-3">Risk Level</th>
                            <th className="text-right p-3">Recommended Order</th>
                          </tr>
                        </thead>
                        <tbody>
                          {forecastData
                            .filter(f => f.riskLevel !== 'low' || f.avgDailySales > 0)
                            .sort((a, b) => (a.daysUntilStockout || 999) - (b.daysUntilStockout || 999))
                            .slice(0, 15)
                            .map((forecast, idx) => (
                              <tr key={idx} className="border-b hover:bg-slate-50">
                                <td className="p-3">
                                  <p className="font-medium">{forecast.productName}</p>
                                  <p className="text-xs text-slate-500">{forecast.category}</p>
                                </td>
                                <td className="text-right p-3">
                                  <span className="font-medium">{forecast.currentStock}</span>
                                  <span className="text-xs text-slate-500 block">
                                    (B: {forecast.bodegaStock}, S: {forecast.shopStock})
                                  </span>
                                </td>
                                <td className="text-right p-3">{forecast.avgDailySales}</td>
                                <td className="text-right p-3">
                                  {forecast.daysUntilStockout ? (
                                    <span className={
                                      forecast.daysUntilStockout < 7 ? 'text-red-600 font-bold' :
                                      forecast.daysUntilStockout < 14 ? 'text-amber-600 font-medium' :
                                      'text-slate-600'
                                    }>
                                      {Math.round(forecast.daysUntilStockout)} days
                                    </span>
                                  ) : (
                                    <span className="text-slate-400">N/A</span>
                                  )}
                                </td>
                                <td className="text-center p-3">
                                  <span className={`px-2 py-1 rounded text-xs font-medium ${
                                    forecast.riskLevel === 'critical' ? 'bg-red-100 text-red-700' :
                                    forecast.riskLevel === 'high' ? 'bg-amber-100 text-amber-700' :
                                    forecast.riskLevel === 'medium' ? 'bg-yellow-100 text-yellow-700' :
                                    'bg-green-100 text-green-700'
                                  }`}>
                                    {forecast.riskLevel}
                                  </span>
                                </td>
                                <td className="text-right p-3">
                                  {forecast.recommendedOrderQty > 0 ? (
                                    <div>
                                      <span className="font-medium text-indigo-600">{forecast.recommendedOrderQty} units</span>
                                      <span className="text-xs text-slate-500 block">
                                        ~{formatCurrency(forecast.estimatedOrderCost)}
                                      </span>
                                    </div>
                                  ) : (
                                    <span className="text-slate-400">-</span>
                                  )}
                                </td>
                              </tr>
                            ))}
                        </tbody>
                      </table>
                    </div>
                  </CardContent>
                </Card>
              )}
              
              {/* Top Products Chart */}
              {salesTrends?.topProducts && salesTrends.topProducts.length > 0 && (
                <Card className="bg-white">
                  <CardHeader>
                    <CardTitle className="text-lg flex items-center gap-2">
                      <PieChart className="text-purple-600" size={20} />
                      Top Selling Products
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="h-72">
                      <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={salesTrends.topProducts.slice(0, 7)} layout="vertical">
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis type="number" tickFormatter={(value) => `₱${(value/1000).toFixed(0)}k`} />
                          <YAxis 
                            dataKey="productName" 
                            type="category" 
                            width={120}
                            tick={{ fontSize: 11 }}
                          />
                          <Tooltip formatter={(value) => [`₱${value.toLocaleString()}`, 'Revenue']} />
                          <Bar dataKey="revenue" fill="#8b5cf6" radius={[0, 4, 4, 0]} />
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                  </CardContent>
                </Card>
              )}
              
              {/* Stock Risk Distribution Chart */}
              {forecastData && forecastData.length > 0 && (
                <Card className="bg-white">
                  <CardHeader>
                    <CardTitle className="text-lg flex items-center gap-2">
                      <AlertTriangle className="text-amber-600" size={20} />
                      Stock Risk Distribution
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="grid md:grid-cols-2 gap-6">
                      <div className="h-64">
                        <ResponsiveContainer width="100%" height="100%">
                          <RechartsPieChart>
                            <Pie
                              data={[
                                { name: 'Critical', value: forecastData.filter(f => f.riskLevel === 'critical').length, fill: '#ef4444' },
                                { name: 'High', value: forecastData.filter(f => f.riskLevel === 'high').length, fill: '#f59e0b' },
                                { name: 'Medium', value: forecastData.filter(f => f.riskLevel === 'medium').length, fill: '#eab308' },
                                { name: 'Low', value: forecastData.filter(f => f.riskLevel === 'low').length, fill: '#22c55e' }
                              ].filter(d => d.value > 0)}
                              cx="50%"
                              cy="50%"
                              innerRadius={50}
                              outerRadius={80}
                              paddingAngle={3}
                              dataKey="value"
                              label={({ name, value }) => `${name}: ${value}`}
                            >
                            </Pie>
                            <Tooltip />
                            <Legend />
                          </RechartsPieChart>
                        </ResponsiveContainer>
                      </div>
                      <div className="flex flex-col justify-center space-y-3">
                        <div className="flex items-center justify-between p-3 bg-red-50 rounded-lg">
                          <span className="font-medium text-red-700">Critical Risk</span>
                          <span className="text-red-700 font-bold">{forecastData.filter(f => f.riskLevel === 'critical').length} products</span>
                        </div>
                        <div className="flex items-center justify-between p-3 bg-amber-50 rounded-lg">
                          <span className="font-medium text-amber-700">High Risk</span>
                          <span className="text-amber-700 font-bold">{forecastData.filter(f => f.riskLevel === 'high').length} products</span>
                        </div>
                        <div className="flex items-center justify-between p-3 bg-yellow-50 rounded-lg">
                          <span className="font-medium text-yellow-700">Medium Risk</span>
                          <span className="text-yellow-700 font-bold">{forecastData.filter(f => f.riskLevel === 'medium').length} products</span>
                        </div>
                        <div className="flex items-center justify-between p-3 bg-green-50 rounded-lg">
                          <span className="font-medium text-green-700">Low Risk</span>
                          <span className="text-green-700 font-bold">{forecastData.filter(f => f.riskLevel === 'low').length} products</span>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              )}
            </>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default NASReports;
